import React, {useEffect, useRef, useState} from 'react'
import {
  fetchMachineryUploadJob,
  MachineryUploadJob,
} from '../../core/_requests'

const STORAGE_KEY = 'machineryUploadJobId'
const HIDE_KEY = 'machineryUploadJobBannerHidden'
const UPLOAD_JOB_POLL_INTERVAL_MS = 20000

const formatEta = (seconds?: number | null) => {
  if (!seconds || seconds <= 0) return null
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}m ${secs}s`
}

export const MachineryUploadJobBanner: React.FC = () => {
  const [job, setJob] = useState<MachineryUploadJob | null>(null)
  const [hiddenJobId, setHiddenJobId] = useState<string | null>(localStorage.getItem(HIDE_KEY))
  const [dismissed, setDismissed] = useState(false)
  const intervalRef = useRef<number | null>(null)

  useEffect(() => {
    let cancelled = false

    const loadInitial = async () => {
      const storedId = localStorage.getItem(STORAGE_KEY)
      if (!storedId) {
        // No stored job ID — skip the active-job probe entirely.
        // This removes the GET /api/machinery/upload/job/active call from
        // the Home dashboard and every other page where no upload was started.
        return
      }

      try {
        const fetched = await fetchMachineryUploadJob(Number(storedId))
        if (!cancelled) {
          setJob(fetched)
        }
      } catch (error) {
        // Stored ID is stale or invalid — clear it
        localStorage.removeItem(STORAGE_KEY)
      }
    }

    loadInitial()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!job || !job.id) return
    const isActive = job.status === 'QUEUED' || job.status === 'RUNNING'

    if (intervalRef.current) {
      window.clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    if (isActive) {
      intervalRef.current = window.setInterval(async () => {
        try {
          const updated = await fetchMachineryUploadJob(job.id as number)
          setJob(updated)
          if (updated.status !== 'QUEUED' && updated.status !== 'RUNNING') {
            localStorage.removeItem(STORAGE_KEY)
          }
        } catch (error) {
          // ignore
        }
      }, UPLOAD_JOB_POLL_INTERVAL_MS)
    }

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [job?.id, job?.status])

  useEffect(() => {
    if (!job?.id) return
    const isHiddenForJob = hiddenJobId === String(job.id)
    setDismissed(isHiddenForJob)
  }, [job?.id, hiddenJobId])

  if (!job || dismissed) return null

  const progress = job.progressPercent ?? 0
  const eta = formatEta(job.etaSeconds)
  const statusLabel =
    job.status === 'SUCCESS'
      ? 'Upload completed'
      : job.status === 'FAILED'
      ? 'Upload failed'
      : job.status === 'CANCELLED'
      ? 'Upload cancelled'
      : 'Upload in progress'

  return (
    <div
      className='bg-white shadow-sm border rounded-3'
      style={{
        position: 'fixed',
        right: 24,
        bottom: 24,
        zIndex: 1200,
        maxWidth: 520,
        width: 'min(520px, calc(100vw - 48px))',
        cursor: 'pointer',
      }}
      role='button'
      tabIndex={0}
      onClick={() => window.dispatchEvent(new Event('open-machinery-upload-modal'))}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          window.dispatchEvent(new Event('open-machinery-upload-modal'))
        }
      }}
    >
      <div className='px-4 py-3 d-flex flex-column gap-2'>
        <div className='d-flex align-items-center gap-2'>
          <div
            className={`rounded-circle d-inline-flex align-items-center justify-content-center ${
              job.status === 'FAILED'
                ? 'bg-danger'
                : job.status === 'SUCCESS'
                ? 'bg-success'
                : job.status === 'CANCELLED'
                ? 'bg-warning'
                : 'bg-primary'
            }`}
            style={{width: 10, height: 10}}
          />
          <div className='fw-semibold text-dark'>{statusLabel}</div>
          <div className='text-muted small ms-auto'>Vessel {job.vesselId}</div>
          <button
            type='button'
            className='btn btn-sm btn-light ms-2'
            onClick={(event) => {
              event.stopPropagation()
              if (job?.id) {
                localStorage.setItem(HIDE_KEY, String(job.id))
                setHiddenJobId(String(job.id))
              }
              setDismissed(true)
            }}
          >
            X
          </button>
        </div>

        <div className='progress' style={{height: 8}}>
          <div
            className={`progress-bar ${
              job.status === 'FAILED' ? 'bg-danger' : job.status === 'CANCELLED' ? 'bg-warning' : 'bg-primary'
            }`}
            role='progressbar'
            style={{width: `${progress}%`}}
          />
        </div>

        <div className='small text-muted'>
          {progress}% {eta ? `- ETA ${eta}` : ''}
          {job.totalRows ? ` - ${job.processedRows ?? 0}/${job.totalRows} rows` : ''}
        </div>
      </div>
    </div>
  )
}
